'use client'

import * as React from 'react'
import { ChevronDown, Menu, type LucideIcon } from 'lucide-react'
import { cn } from '../../utils'
import {
  Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerTitle, DrawerTrigger,
} from '../ui/drawer'

// ── Shell ─────────────────────────────────────────────────────────────────────

interface TopbarProps extends React.HTMLAttributes<HTMLElement> {
  logo: React.ReactNode
  /** Desktop nav (rendered inline). Hidden below the `lg` breakpoint. */
  nav?: React.ReactNode
  right?: React.ReactNode
  /**
   * Mobile nav contents. When provided, a hamburger button renders to the
   * left of the logo on viewports below `lg` and opens a left-anchored
   * Drawer containing this content. Apps typically pass <MobileNavLink/>
   * and <MobileNavGroup/> children that mirror their desktop nav items.
   */
  mobileNav?: React.ReactNode
  /** Title shown at the top of the open mobile-nav drawer. */
  mobileNavTitle?: string
}

/**
 * Shared topbar shell. Accepts logo / nav / right / mobileNav slots.
 * Apps compose their own nav items using TopbarNavLink / TopbarNavDropdown
 * for desktop and MobileNavLink / MobileNavGroup for the mobile drawer.
 */
function Topbar({
  logo, nav, right, mobileNav, mobileNavTitle = 'Menu', className, ...props
}: TopbarProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false)

  // Close the mobile drawer whenever the user navigates (a child link click
  // typically changes the URL but doesn't unmount the Topbar).
  const handleNavClick = React.useCallback(() => setMobileOpen(false), [])

  return (
    <header
      className={cn(
        // `sticky top-0` only pins along the y-axis. Without a width clamp
        // the Topbar inherits the body's width — if any descendant pushes
        // the page wider than the viewport, the Topbar goes with it and
        // its right-side actions slide off-screen on phones. `w-screen
        // max-w-full` keeps it locked to the visible viewport.
        'sticky top-0 z-40 w-screen max-w-full bg-transparent px-[var(--space-outer)] pt-[var(--space-outer)]',
        className
      )}
      {...props}
    >
      <div className="grid h-[var(--size-segment)] grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center px-[var(--space-outer)]">
        <div className="flex min-w-0 items-center gap-1 justify-self-start">
          {mobileNav && (
            <Drawer open={mobileOpen} onOpenChange={setMobileOpen}>
              <DrawerTrigger asChild>
                <button
                  type="button"
                  aria-label="Open navigation menu"
                  className={cn(
                    'lg:hidden inline-flex items-center justify-center',
                    'h-10 w-10 mr-1 rounded-xl bg-card',
                    'text-muted-foreground hover:text-foreground hover:bg-muted',
                    'focus:outline-none focus:ring-2 focus:ring-ring transition-colors'
                  )}
                >
                  <Menu className="h-5 w-5" />
                </button>
              </DrawerTrigger>
              <DrawerContent side="left" width="sm" className="p-0">
                <DrawerHeader>
                  <DrawerTitle>{mobileNavTitle}</DrawerTitle>
                </DrawerHeader>
                <DrawerBody className="px-3 py-3" onClickCapture={handleNavClick}>
                  <div className="flex flex-col gap-1">
                    {mobileNav}
                  </div>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          )}
          <div className="flex h-10 min-w-10 items-center justify-center">
            {logo}
          </div>
        </div>
        {nav && (
          <nav className="hidden min-w-0 items-center gap-1 border-0 bg-transparent p-0 shadow-none lg:flex">
            {nav}
          </nav>
        )}
        {right && <div className="flex items-center gap-2 justify-self-end">{right}</div>}
      </div>
    </header>
  )
}

// ── Nav link ──────────────────────────────────────────────────────────────────

interface TopbarNavLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string
  active?: boolean
  icon?: LucideIcon
  children: React.ReactNode
  /** Render via Next.js Link — pass your own Link as the wrapping element via `asChild` pattern. */
  as?: React.ElementType
}

/**
 * Compact desktop nav link for the shared topbar surface. Inactive destinations
 * stay quiet while the current page becomes the single filled focal point.
 * Pass `as={Link}` (from next/link) for client-side routing.
 */
function TopbarNavLink({
  href, active = false, icon: _icon, children, className, as: Comp = 'a', ...props
}: TopbarNavLinkProps) {
  return (
    <Comp
      href={href}
      className={cn(
        'relative flex h-10 items-center justify-center rounded-[var(--radius-sm)] px-4',
        'whitespace-nowrap text-[12px] leading-none transition-colors cursor-pointer',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'bg-primary font-semibold text-primary-foreground'
          : 'bg-transparent font-medium text-muted-foreground hover:text-foreground',
        className
      )}
      {...props}
    >
      <span>{children}</span>
    </Comp>
  )
}

// ── Nav dropdown (hover) ───────────────────────────────────────────────────────

interface TopbarNavDropdownItem {
  href: string
  label: string
  icon?: LucideIcon
  active?: boolean
  as?: React.ElementType
}

interface TopbarNavDropdownProps {
  label: string
  icon?: LucideIcon
  active?: boolean
  items: TopbarNavDropdownItem[]
  className?: string
}

/**
 * Hover dropdown for grouped nav items (e.g. Fleet → Drivers / Vehicles).
 */
function TopbarNavDropdown({ label, icon: _icon, active = false, items, className }: TopbarNavDropdownProps) {
  return (
    <div className={cn('relative group flex items-center', className)}>
      <button
        className={cn(
          'relative flex h-10 items-center justify-center rounded-[var(--radius-sm)] px-4',
          'whitespace-nowrap text-[12px] leading-none transition-colors cursor-pointer',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          active
            ? 'bg-primary font-semibold text-primary-foreground'
            : 'bg-transparent font-medium text-muted-foreground hover:text-foreground'
        )}
      >
        <span className="flex items-center gap-0.5 leading-none">
          {label}
          <ChevronDown className="size-2.5 transition-transform duration-200 group-hover:rotate-180" />
        </span>
      </button>

      <div className="absolute top-full left-0 mt-1 hidden group-hover:block bg-popover text-popover-foreground border border-border rounded-[var(--radius-sm)] shadow-md z-50 min-w-[160px] py-1">
        {items.map((item) => {
          const Comp = item.as ?? 'a'
          return (
            <Comp
              key={item.href}
              href={item.href}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-[12.5px] transition-colors',
                item.active
                  ? 'text-primary font-medium bg-primary/5'
                  : 'text-foreground hover:bg-muted'
              )}
            >
              {item.icon && <item.icon className="w-4 h-4 shrink-0" />}
              {item.label}
            </Comp>
          )
        })}
      </div>
    </div>
  )
}

export { Topbar, TopbarNavLink, TopbarNavDropdown }
export type { TopbarProps, TopbarNavLinkProps, TopbarNavDropdownProps, TopbarNavDropdownItem }
